// --- src/data/m3/Unit8.js ---

export const M3_UNIT_8 = {
  id: "m3-u8-likes-preferences",
  title: "Unit 8: Likes & Preferences",
  topic: "Express preferences, describe activities, ask about likes with reasons",
  iconType: "heart",
  grammarImage: "/m3-u8-preferences.jpg",
  grammarText: "Express what you love, like, don't like, hate, and can't stand. Ask and answer about preferences with reasons using because, so, and but. Describe activities with adjectives.",
  sets: [

    // ===================================================================
    // SET 1: PREFERENCE SCALE (A2)
    // ===================================================================
    {
      id: "m3-u8-set1",
      title: "Set 1: Expressing Preferences 😍😐😡",
      context: `*Lunes - Cafetería de la universidad*

Emma es estudiante en Toronto. Hoy habla con amigos sobre sus preferencias.
"Me encanta el café. Me gusta el té. No me importa el jugo. No me gusta la leche. Odio los refrescos."
Aprenden la escala completa de preferencias.

*Monday - University cafeteria*

Emma is a student in Toronto. Today she talks with friends about her preferences.
"I love coffee. I like tea. I don't mind juice. I don't like milk. I hate soda."
They learn the complete preference scale.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match the expression to its intensity.",
          pairs: [
            { left: "I love it ❤️", right: "100% positive" },
            { left: "I don't mind it 😐", right: "Neutral/OK" },
            { left: "I can't stand it 🤮", right: "Extremely negative" }
          ],
          a: "completed",
          tip: "Scale from love to hate!",
          successTip: "Preference scale! From love (strongest +) to can't stand (strongest -)! 📊"
        },
        {
          type: "mc",
          q: "Emma drinks coffee every morning with excitement. She ___ coffee.",
          options: ["loves", "doesn't mind", "hates"],
          a: "loves",
          tip: "Every morning + excitement = strong positive!",
          successTip: "Loves! Strongest positive feeling! ❤️"
        },
        {
          type: "mc",
          q: "Tom is allergic to seafood and feels sick when he sees it. He ___ seafood.",
          options: ["can't stand", "doesn't like", "doesn't mind"],
          a: "can't stand",
          tip: "Allergic + sick = extremely negative!",
          successTip: "Can't stand! Strongest negative! Even stronger than 'hate'! 🤮"
        },
        {
          type: "match",
          q: "Match the complete preference scale.",
          pairs: [
            { left: "Love → Like → Don't mind", right: "Positive to neutral" },
            { left: "Don't like → Hate", right: "Negative" },
            { left: "Can't stand", right: "Extremely negative" }
          ],
          a: "completed",
          tip: "Full scale from best to worst!",
          successTip: "Complete scale! 6 levels of preference! 📈"
        },
        {
          type: "input",
          q: "Sarah drinks orange juice sometimes, but it's not her favorite. She ___ ___ orange juice.",
          a: "doesn't mind",
          alts: ["likes"],
          tip: "Not favorite but OK = neutral!",
          successTip: "Doesn't mind! Neutral feeling = it's OK! 😐"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Me encanta el chocolate.'",
          words: ["I", "love", "chocolate."],
          a: "I love chocolate.",
          extra: ["like", "am", "loving"],
          tip: "Love = strongest positive!"
        },
        {
          type: "mc",
          q: "Mike is vegetarian and refuses to eat meat. He ___ meat.",
          options: ["can't stand", "doesn't mind", "likes"],
          a: "can't stand",
          tip: "Refuses = extremely negative!",
          successTip: "Can't stand! Vegetarian = very strong feeling! 🥗"
        },
        {
          type: "input",
          q: "Lisa sometimes watches TV, but she prefers reading. She ___ watching TV.",
          a: "doesn't mind",
          alts: ["likes"],
          tip: "OK but not preferred!",
          successTip: "Doesn't mind! It's acceptable but not favorite! 📺"
        },
        {
          type: "order",
          q: "Traduce: 'Odio levantarme temprano.'",
          words: ["I", "hate", "waking", "up", "early."],
          a: "I hate waking up early.",
          extra: ["wake", "to wake", "am"],
          tip: "Hate = strong negative!"
        },
        {
          type: "mc",
          q: "Order from most positive to most negative:",
          options: ["love, like, don't mind, don't like, hate, can't stand", "like, love, hate, don't mind", "can't stand, hate, don't like"],
          a: "love, like, don't mind, don't like, hate, can't stand",
          tip: "Complete scale!",
          successTip: "Perfect scale! 6 levels mastered! Love (best) → can't stand (worst)! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 2: ASKING & ANSWERING ABOUT LIKES (A2)
    // ===================================================================
    {
      id: "m3-u8-set2",
      title: "Set 2: Do You Like...? Why? ❓",
      context: `*Martes - Clase de conversación*

Marco es profesor en Vancouver. Hoy enseña a hacer y responder preguntas.
"¿Te gusta el fútbol? Sí, me encanta porque es emocionante."
Los estudiantes practican preguntas y respuestas con razones.

*Tuesday - Conversation class*

Marco is a teacher in Vancouver. Today he teaches how to ask and answer questions.
"Do you like soccer? Yes, I love it because it's exciting."
Students practice questions and answers with reasons.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "mc",
          q: "Question: 'Do you like pizza?' Best complete answer:",
          options: ["Yes, I love it because it's delicious", "Yes", "Pizza is good"],
          a: "Yes, I love it because it's delicious",
          tip: "Complete answer = yes/no + feeling + reason!",
          successTip: "Complete answer! Yes + feeling + because + reason! 🍕"
        },
        {
          type: "match",
          q: "Match question word to what it asks.",
          pairs: [
            { left: "Do you like...?", right: "Yes/No + feeling" },
            { left: "Why?", right: "Reason (because...)" },
            { left: "What do you like?", right: "Name things you like" }
          ],
          a: "completed",
          tip: "Different question types!",
          successTip: "Question types! Each asks different information! 🎯"
        },
        {
          type: "mc",
          q: "'Do you like coffee?' - 'No, I hate it.' Why might they hate it?",
          options: ["Because it's bitter", "Because it's delicious", "Because it's healthy"],
          a: "Because it's bitter",
          tip: "Hate = negative reason!",
          successTip: "Bitter! Negative reason matches negative feeling! ☕"
        },
        {
          type: "input",
          q: "Answer: 'Do you like swimming?' → Yes, I love ___ because ___ fun.",
          a: "it it's",
          alts: ["it its"],
          tip: "It (object pronoun) + it's (it is)!",
          successTip: "It + it's! Pronoun + reason! Both forms of 'it'! 🏊"
        },
        {
          type: "mc",
          q: "'Why do you like running?' Best answer:",
          options: ["Because it's good exercise", "Yes, I like it", "Running is fun"],
          a: "Because it's good exercise",
          tip: "Why = because + reason!",
          successTip: "Because! 'Why' questions need 'because' answers! 🏃"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: '¿Te gusta el chocolate?'",
          words: ["Do", "you", "like", "chocolate?"],
          a: "Do you like chocolate?",
          extra: ["Are", "Does", "Is"],
          tip: "Do you like + thing!"
        },
        {
          type: "input",
          q: "Answer with reason: 'Do you like winter?' → No, I ___ it because it's too cold.",
          a: "hate",
          alts: ["don't like", "can't stand"],
          tip: "Too cold = negative reason!",
          successTip: "Negative feeling! Reason matches feeling! ❄️"
        },
        {
          type: "order",
          q: "Traduce: 'Sí, me encanta porque es emocionante.'",
          words: ["Yes,", "I", "love", "it", "because", "it's", "exciting."],
          a: "Yes, I love it because it's exciting.",
          extra: ["am", "so", "and"],
          tip: "Complete answer with reason!"
        },
        {
          type: "mc",
          q: "Complete conversation: 'Do you like vegetables?' - 'No, ___'",
          options: ["I can't stand them because they taste awful", "I like them", "They are healthy"],
          a: "I can't stand them because they taste awful",
          tip: "No = negative feeling + reason!",
          successTip: "Complete negative answer! Feeling + pronoun + because! 🥦"
        },
        {
          type: "input",
          q: "Ask the question: '___ ___ ___ cooking?' (Do you like)",
          a: "Do you like",
          alts: [],
          tip: "Do you like + activity!",
          successTip: "Do you like! Question form mastered! ❓"
        }
      ]
    },

    // ===================================================================
    // SET 3: DESCRIBING ACTIVITIES WITH ADJECTIVES (A2)
    // ===================================================================
    {
      id: "m3-u8-set3",
      title: "Set 3: Describing Activities 🎨",
      context: `*Miércoles - Club de actividades*

Yuki es coordinadora en Montreal. Hoy describe diferentes actividades.
"El yoga es relajante. El fútbol es emocionante. La limpieza es aburrida."
Usa adjetivos para describir actividades.

*Wednesday - Activities club*

Yuki is a coordinator in Montreal. Today she describes different activities.
"Yoga is relaxing. Soccer is exciting. Cleaning is boring."
She uses adjectives to describe activities.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match activity to typical adjective.",
          pairs: [
            { left: "Yoga 🧘", right: "relaxing, peaceful" },
            { left: "Soccer ⚽", right: "exciting, competitive" },
            { left: "Cleaning 🧹", right: "boring, tiring" }
          ],
          a: "completed",
          tip: "Common activity descriptions!",
          successTip: "Activity adjectives! Common associations! 🎯"
        },
        {
          type: "mc",
          q: "Swimming makes you feel calm and peaceful. Swimming is:",
          options: ["relaxing", "exciting", "boring"],
          a: "relaxing",
          tip: "Calm + peaceful = relaxing!",
          successTip: "Relaxing! Calm feeling = relaxing activity! 🏊"
        },
        {
          type: "mc",
          q: "Horror movies make your heart beat fast and you feel scared. They are:",
          options: ["scary", "relaxing", "boring"],
          a: "scary",
          tip: "Fast heart + scared = scary!",
          successTip: "Scary! Fear feeling = scary activity! 😱"
        },
        {
          type: "match",
          q: "Match feeling to adjective type.",
          pairs: [
            { left: "Positive feelings 😊", right: "fun, exciting, relaxing" },
            { left: "Negative feelings 😞", right: "boring, tiring, difficult" },
            { left: "Neutral/Mixed 😐", right: "interesting, challenging" }
          ],
          a: "completed",
          tip: "Adjectives show feelings!",
          successTip: "Feeling-adjective connection! Describes experience! 💭"
        },
        {
          type: "input",
          q: "Running makes you tired and you breathe hard. Running is:",
          a: "tiring",
          alts: ["exhausting", "difficult"],
          tip: "Makes tired = tiring!",
          successTip: "Tiring! Physical exhaustion = tiring activity! 🏃"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'El yoga es relajante.'",
          words: ["Yoga", "is", "relaxing."],
          a: "Yoga is relaxing.",
          extra: ["relaxed", "relax", "a"],
          tip: "Activity + is + adjective!"
        },
        {
          type: "mc",
          q: "Video games are full of action and make you excited. They are:",
          options: ["exciting", "boring", "relaxing"],
          a: "exciting",
          tip: "Action + excited = exciting!",
          successTip: "Exciting! Action and excitement! 🎮"
        },
        {
          type: "input",
          q: "Complete: 'I love swimming because it's ___ and ___.' (2 positive adjectives)",
          a: "relaxing fun",
          alts: ["fun relaxing", "healthy enjoyable", "peaceful refreshing"],
          tip: "Any two positive adjectives!",
          successTip: "Two adjectives! Gives fuller description! 🌟"
        },
        {
          type: "order",
          q: "Traduce: 'La limpieza es aburrida y cansadora.'",
          words: ["Cleaning", "is", "boring", "and", "tiring."],
          a: "Cleaning is boring and tiring.",
          extra: ["bored", "tired", "a"],
          tip: "Two negative adjectives with 'and'!"
        },
        {
          type: "input",
          q: "Complete with adjective: 'I don't like running because it's too ___.' (negative)",
          a: "tiring",
          alts: ["difficult", "hard", "exhausting", "boring"],
          tip: "Any negative adjective!",
          successTip: "Negative adjective! Explains dislike! Perfect! 🎯"
        }
      ]
    },

    // ===================================================================
    // SET 4: USING CONNECTORS (A2+)
    // ===================================================================
    {
      id: "m3-u8-set4",
      title: "Set 4: Because, So, But, And 🔗",
      context: `*Jueves - Taller de conversación*

Daniel es estudiante en Calgary. Hoy practica conectar ideas.
"Me encanta la pizza porque es deliciosa. Es rica, pero no es saludable. Es poco saludable, entonces no la como mucho."
Conecta ideas con razones y contrastes.

*Thursday - Conversation workshop*

Daniel is a student in Calgary. Today he practices connecting ideas.
"I love pizza because it's delicious. It's tasty, but it's not healthy. It's unhealthy, so I don't eat it much."
He connects ideas with reasons and contrasts.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "mc",
          q: "'I love coffee because it's energizing.' What does 'because' show?",
          options: ["The reason for loving it", "The result of loving it", "A contrast"],
          a: "The reason for loving it",
          tip: "Because = gives reason!",
          successTip: "Because! Shows WHY you feel that way! Reason/cause! ☕"
        },
        {
          type: "mc",
          q: "'I like pizza, but it's unhealthy.' What does 'but' show?",
          options: ["A contrast/problem", "A reason", "A result"],
          a: "A contrast/problem",
          tip: "But = shows opposite/problem!",
          successTip: "But! Like it (positive) vs unhealthy (negative) = contrast! 🍕"
        },
        {
          type: "match",
          q: "Match connector to its function.",
          pairs: [
            { left: "because", right: "reason (porque)" },
            { left: "so", right: "result (entonces)" },
            { left: "but", right: "contrast (pero)" }
          ],
          a: "completed",
          tip: "Three main connectors!",
          successTip: "Connectors link ideas logically! 🔗"
        },
        {
          type: "mc",
          q: "'Exercise is healthy, so I work out every day.' What does 'so' show?",
          options: ["The result/action", "The reason", "A contrast"],
          a: "The result/action",
          tip: "So = shows result/consequence!",
          successTip: "So! Healthy (reason) → work out (result/action)! 💪"
        },
        {
          type: "input",
          q: "Complete logic: 'Swimming is fun ___ relaxing.' (connect two similar ideas)",
          a: "and",
          alts: [],
          tip: "And = adds similar ideas!",
          successTip: "And! Links similar positive ideas together! 🏊"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Me encanta el chocolate porque es dulce.'",
          words: ["I", "love", "chocolate", "because", "it's", "sweet."],
          a: "I love chocolate because it's sweet.",
          extra: ["so", "but", "and"],
          tip: "Because = reason!"
        },
        {
          type: "mc",
          q: "Complete: 'I love burgers, ___ they're unhealthy, ___ I don't eat them often.'",
          options: ["but // so", "because // and", "and // but"],
          a: "but // so",
          tip: "Love BUT unhealthy (contrast) SO don't eat often (result)!",
          successTip: "But + so! Contrast then result! Logical chain! 🍔"
        },
        {
          type: "input",
          q: "Complete: 'I hate cleaning ___ it's boring.' (reason)",
          a: "because",
          alts: [],
          tip: "Hate = feeling, boring = reason!",
          successTip: "Because! Connects feeling to reason! 🧹"
        },
        {
          type: "order",
          q: "Traduce: 'El yoga es relajante, entonces lo hago cada noche.'",
          words: ["Yoga", "is", "relaxing,", "so", "I", "do", "it", "every", "night."],
          a: "Yoga is relaxing, so I do it every night.",
          extra: ["because", "but", "and"],
          tip: "So = result/action!"
        },
        {
          type: "input",
          q: "Complete: 'I like tea ___ I love coffee.' (contrast two preferences)",
          a: "but",
          alts: [],
          tip: "Like vs love = different levels!",
          successTip: "But! Contrasts two different feelings! Like < love! ☕🍵"
        }
      ]
    },

    // ===================================================================
    // SET 5: MY FAVORITE THINGS (A2+)
    // ===================================================================
    {
      id: "m3-u8-set5",
      title: "Set 5: My Favorite Things ⭐",
      context: `*Viernes - Presentaciones personales*

Sofia es estudiante nueva en Ottawa. Hoy presenta sus favoritos.
"Mi comida favorita es la pasta. Mi deporte favorito es la natación. Mi hobby número uno es leer."
Comparte sus preferencias principales.

*Friday - Personal presentations*

Sofia is a new student in Ottawa. Today she presents her favorites.
"My favorite food is pasta. My favorite sport is swimming. My number one hobby is reading."
She shares her main preferences.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match favorite expression to meaning.",
          pairs: [
            { left: "My favorite...", right: "The one I like most" },
            { left: "My number one...", right: "The most important one" },
            { left: "The best...", right: "Better than all others" }
          ],
          a: "completed",
          tip: "Three ways to express top choice!",
          successTip: "Favorite expressions! All mean 'the one I like most'! ⭐"
        },
        {
          type: "mc",
          q: "Sofia says: 'My favorite food is pasta.' This means:",
          options: ["She likes pasta more than other foods", "She only likes pasta", "She eats pasta every day"],
          a: "She likes pasta more than other foods",
          tip: "Favorite = likes most!",
          successTip: "Favorite! Likes it MORE than others (not only it)! 🍝"
        },
        {
          type: "mc",
          q: "'Reading is my number one hobby.' Which is true?",
          options: ["It's the most important hobby for them", "It's their only hobby", "They read all the time"],
          a: "It's the most important hobby for them",
          tip: "Number one = most important!",
          successTip: "Number one! Most important, but may have others! 📚"
        },
        {
          type: "input",
          q: "Complete: 'Of all sports, I like swimming ___.' (more than all others)",
          a: "best",
          alts: ["the most", "most"],
          tip: "Best = more than all others!",
          successTip: "Best! Comparative - better than all others! 🏊"
        },
        {
          type: "match",
          q: "Match question to answer pattern.",
          pairs: [
            { left: "What's your favorite food?", right: "My favorite food is..." },
            { left: "What food do you like best?", right: "I like ... best" },
            { left: "What's your number one sport?", right: "My number one sport is..." }
          ],
          a: "completed",
          tip: "Question-answer patterns!",
          successTip: "Matching patterns! Natural responses! 🎯"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Mi comida favorita es la pizza.'",
          words: ["My", "favorite", "food", "is", "pizza."],
          a: "My favorite food is pizza.",
          extra: ["the", "most", "best"],
          tip: "My favorite + category + is + thing!"
        },
        {
          type: "mc",
          q: "Answer: 'What's your favorite sport?' (Choose pattern)",
          options: ["My favorite sport is soccer", "I like sport is soccer", "Soccer is favorite"],
          a: "My favorite sport is soccer",
          tip: "My favorite + category + is + answer!",
          successTip: "My favorite sport is! Complete answer pattern! ⚽"
        },
        {
          type: "input",
          q: "Complete: 'Swimming is my ___ ___ activity.' (number one)",
          a: "number one",
          alts: ["favorite"],
          tip: "Number one = top choice!",
          successTip: "Number one! Emphasizes it's #1! 🏊"
        },
        {
          type: "order",
          q: "Traduce: 'De todos los deportes, me gusta más el tenis.'",
          words: ["Of", "all", "sports,", "I", "like", "tennis", "best."],
          a: "Of all sports, I like tennis best.",
          extra: ["most", "the", "better"],
          tip: "Of all... I like ... best!"
        },
        {
          type: "input",
          q: "Answer about YOU: 'What's your favorite food?' → My ___ ___ is ___.",
          a: "favorite food",
          alts: ["favorite food pizza", "favorite food pasta", "favorite food sushi"],
          tip: "Answer about your real preference!",
          successTip: "Personal answer! My favorite food is... Perfect! 🍽️"
        }
      ]
    },

    // ===================================================================
    // SET 6: HOBBIES WITH GERUNDS (A2+)
    // ===================================================================
    {
      id: "m3-u8-set6",
      title: "Set 6: Talking About Hobbies 🎨",
      context: `*Sábado - Centro comunitario*

Alex organiza talleres en Halifax. Hoy habla de pasatiempos.
"Me gusta leer. Disfruto nadar. Amo cocinar."
Usa gerundios para hablar de actividades.

*Saturday - Community center*

Alex organizes workshops in Halifax. Today he talks about hobbies.
"I like reading. I enjoy swimming. I love cooking."
He uses gerunds to talk about activities.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match hobby verb to gerund form.",
          pairs: [
            { left: "swim", right: "swimming" },
            { left: "read", right: "reading" },
            { left: "cook", right: "cooking" }
          ],
          a: "completed",
          tip: "Verb + -ing = gerund!",
          successTip: "Gerund formation! Verb + -ing for activities! 🎯"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: ["I enjoy swimming", "I enjoy to swim", "I enjoy swim"],
          a: "I enjoy swimming",
          tip: "After 'enjoy' use -ing!",
          successTip: "I enjoy swimming! After 'enjoy/love/like', use -ing form! 🏊"
        },
        {
          type: "mc",
          q: "'I love reading because it's relaxing.' Reading here is:",
          options: ["A gerund (activity/hobby)", "A verb action happening now", "A person who reads"],
          a: "A gerund (activity/hobby)",
          tip: "Reading = the activity itself!",
          successTip: "Gerund! Acts like a noun - names the activity! 📚"
        },
        {
          type: "match",
          q: "Match verb pattern to example.",
          pairs: [
            { left: "love/like/enjoy + -ing", right: "I love swimming" },
            { left: "hate/can't stand + -ing", right: "I hate cleaning" },
            { left: "don't mind + -ing", right: "I don't mind cooking" }
          ],
          a: "completed",
          tip: "All preference verbs use -ing!",
          successTip: "Pattern! All preference verbs + gerund (-ing)! 🎯"
        },
        {
          type: "input",
          q: "Complete: 'I like ___ books.' (gerund of 'read')",
          a: "reading",
          alts: [],
          tip: "Read + -ing!",
          successTip: "Reading! Like + gerund for hobbies! 📖"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Me encanta cocinar.'",
          words: ["I", "love", "cooking."],
          a: "I love cooking.",
          extra: ["cook", "to cook", "am"],
          tip: "Love + -ing form!"
        },
        {
          type: "mc",
          q: "Complete: 'She enjoys ___ to music.'",
          options: ["listening", "listen", "to listen"],
          a: "listening",
          tip: "Enjoy + -ing!",
          successTip: "Listening! Enjoy + gerund! 🎵"
        },
        {
          type: "input",
          q: "Complete: 'I hate ___ early.' (gerund of wake up)",
          a: "waking up",
          alts: ["waking"],
          tip: "Wake up → waking up!",
          successTip: "Waking up! Hate + gerund! Phrasal verb = both words + -ing! ⏰"
        },
        {
          type: "order",
          q: "Traduce: 'Disfruto nadar en el océano.'",
          words: ["I", "enjoy", "swimming", "in", "the", "ocean."],
          a: "I enjoy swimming in the ocean.",
          extra: ["swim", "to swim", "am"],
          tip: "Enjoy + swimming!"
        },
        {
          type: "input",
          q: "Complete with reason: 'I love ___ because it's ___.' (activity + adjective)",
          a: "reading relaxing",
          alts: ["swimming fun", "cooking creative", "dancing exciting", "running healthy"],
          tip: "Any hobby + matching adjective!",
          successTip: "Gerund + reason! Express your real hobby! Perfect! 🌟"
        }
      ]
    },

    // ===================================================================
    // SET 7: DIALOGUE - FOOD PREFERENCES 💬
    // ===================================================================
    {
      id: "m3-u8-set7",
      title: "Set 7: Dialogue - Food Talk 💬",
      context: `*Domingo - Restaurante*

Ana y Ben son amigos en Toronto. Hoy hablan sobre comida.
"¿Cuál es tu comida favorita?" pregunta Ana.
Tú participas completando la conversación.

*Sunday - Restaurant*

Ana and Ben are friends in Toronto. Today they talk about food.
"What's your favorite food?" asks Ana.
You participate by completing the conversation.`,
      
      questions: [
        {
          type: "passage",
          passage: `**Restaurant Conversation**

Ana: "What's your favorite food?"
Ben: "..."
Ana: "Oh, I love pizza too! Why do you like it?"
Ben: "..."`
        },
        {
          type: "input",
          q: "Complete Ben's answer: 'My favorite food is ___.'",
          a: "pizza",
          alts: ["pasta", "sushi", "burgers", "tacos"],
          tip: "Choose any food!",
          successTip: "✓ FAVORITE!\nAny food works! Personal preference! 🍕"
        },
        {
          type: "mc",
          q: "Complete Ben's reason: 'Because ___'",
          options: ["it's delicious", "I am hungry", "pizza is food"],
          a: "it's delicious",
          tip: "Give a real reason!",
          successTip: "✓ REASON!\nBecause + adjective reason! Perfect! 😋"
        },
        {
          type: "passage",
          passage: `**Continuing**

Ana: "I agree! Do you like Japanese food?"
Ben: "..."
Ana: "Really? Why not?"
Ben: "..."`
        },
        {
          type: "mc",
          q: "Complete Ben's negative answer: '___'",
          options: ["No, I don't like it very much", "Yes, I love it", "Japanese food is good"],
          a: "No, I don't like it very much",
          tip: "She asks 'why not' → he said no!",
          successTip: "✓ NEGATIVE!\nNo + I don't like it! Complete answer! 🍱"
        },
        {
          type: "input",
          q: "Complete reason: 'Because I find ___ too ___.' (it, adjective)",
          a: "it strange",
          alts: ["it different", "it spicy", "it expensive"],
          tip: "Any negative reason!",
          successTip: "✓ REASON!\nI find it + adjective! Sophisticated! 🎯"
        },
        {
          type: "passage",
          passage: `**Ana's Turn**

Ben: "What about you? What food do you hate?"
Ana: "..."
Ben: "Really? Why?"
Ana: "..."`
        },
        {
          type: "input",
          q: "Complete Ana's answer: 'I ___ ___ broccoli.' (can't stand)",
          a: "can't stand",
          alts: ["hate"],
          tip: "Strongest negative!",
          successTip: "✓ CAN'T STAND!\nStrongest dislike! Perfect strong expression! 🥦"
        },
        {
          type: "order",
          q: "Complete Ana's reason.",
          words: ["Because", "it", "tastes", "awful."],
          a: "Because it tastes awful.",
          extra: ["is", "has", "makes"],
          tip: "Tastes = has a taste of!"
        },
        {
          type: "passage",
          passage: `**Final Exchange**

Ana: "Do you like cooking?"
Ben: "..."
Ana: "That's great! What do you like cooking?"
Ben: "..."`
        },
        {
          type: "mc",
          q: "Complete Ben's answer: 'Yes, I ___ cooking.'",
          options: ["enjoy", "am enjoying", "to enjoy"],
          a: "enjoy",
          tip: "Enjoy + gerund!",
          successTip: "✓ ENJOY!\nEnjoy + -ing form! 🍳"
        },
        {
          type: "input",
          q: "Complete final answer: 'I like cooking ___ because ___.' (food + reason)",
          a: "pasta it's easy",
          alts: ["pizza it's fun", "tacos they're delicious", "salads they're healthy"],
          tip: "Any food + reason!",
          successTip: "✓ COMPLETE!\nFood + pronoun + reason! Dialogue complete! 🎉"
        }
      ]
    },

    // ===================================================================
    // SET 8: DIALOGUE - SPORTS & ACTIVITIES 🎯
    // ===================================================================
    {
      id: "m3-u8-set8",
      title: "Set 8: Dialogue - Sports Talk 🎯",
      context: `*Lunes - Gimnasio*

Carlos y Lisa hablan en el gimnasio de Vancouver.
"¿Qué deportes te gustan?" pregunta Carlos.
Practica conversaciones sobre deportes y actividades.

*Monday - Gym*

Carlos and Lisa talk at the gym in Vancouver.
"What sports do you like?" asks Carlos.
Practice conversations about sports and activities.`,
      
      questions: [
        {
          type: "passage",
          passage: `**Gym Talk**

Carlos: "What sports do you like?"
Lisa: "..."
Carlos: "Nice! Why do you like it?"
Lisa: "..."`
        },
        {
          type: "input",
          q: "Complete Lisa's answer: 'I love ___.' (-ing form)",
          a: "swimming",
          alts: ["running", "playing soccer", "playing tennis", "cycling"],
          tip: "Sport with -ing!",
          successTip: "✓ GERUND!\nLove + -ing form for activities! 🏊"
        },
        {
          type: "mc",
          q: "Complete reason: 'Because it's ___'",
          options: ["relaxing and good exercise", "a sport", "in the water"],
          a: "relaxing and good exercise",
          tip: "Two adjective reasons!",
          successTip: "✓ TWO REASONS!\nBecause + two adjectives! Fuller answer! 💪"
        },
        {
          type: "passage",
          passage: `**More Questions**

Lisa: "What about you? Do you like running?"
Carlos: "..."
Lisa: "Oh, why not?"
Carlos: "..."`
        },
        {
          type: "mc",
          q: "Complete Carlos's negative answer: '___'",
          options: ["No, I can't stand it", "Yes, I love it", "Running is good"],
          a: "No, I can't stand it",
          tip: "She asks 'why not' → he dislikes it!",
          successTip: "✓ CAN'T STAND!\nStrongest negative response! 🏃"
        },
        {
          type: "input",
          q: "Complete reason: 'Because I find it ___ and ___.' (two negative adjectives)",
          a: "boring tiring",
          alts: ["tiring boring", "difficult exhausting"],
          tip: "Two negative reasons!",
          successTip: "✓ TWO NEGATIVES!\nI find it + two adjectives! 🎯"
        },
        {
          type: "passage",
          passage: `**Sports Comparison**

Carlos: "I love playing soccer because it's exciting."
Lisa: "I like soccer, but I prefer swimming."
Carlos: "Why do you prefer swimming?"
Lisa: "..."`
        },
        {
          type: "order",
          q: "Complete Lisa's comparison.",
          words: ["Because", "it's", "more", "relaxing", "and", "peaceful."],
          a: "Because it's more relaxing and peaceful.",
          extra: ["most", "very", "much"],
          tip: "More + adjective for comparison!"
        },
        {
          type: "mc",
          q: "Complete: 'Soccer is fun, ___ swimming is better for me.'",
          options: ["but", "so", "because"],
          a: "but",
          tip: "Contrasting two preferences!",
          successTip: "✓ BUT!\nLike both, but prefer one! Contrast! ⚽🏊"
        },
        {
          type: "passage",
          passage: `**Final Question**

Lisa: "What's your favorite sport?"
Carlos: "..."
Lisa: "That's great! You must be really good!"
Carlos: "Thanks!"`
        },
        {
          type: "input",
          q: "Complete Carlos's answer: 'My ___ ___ is soccer because it's ___.'",
          a: "favorite sport exciting",
          alts: ["number one sport exciting", "favorite sport fun"],
          tip: "Favorite + sport + reason!",
          successTip: "✓ COMPLETE!\nFavorite + reason! Perfect sports conversation! ⚽"
        }
      ]
    },

    // ===================================================================
    // SET 9: OPINION EXPRESSIONS - SIMPLIFIED (A2-B1)
    // ===================================================================
    {
      id: "m3-u8-set9",
      title: "Set 9: Expressing Opinions 💭",
      context: `*Martes - Debate en clase*

La profesora Chen enseña expresiones de opinión en Winnipeg.
"Yo creo que... En mi opinión... Yo encuentro..."
Los estudiantes aprenden diferentes formas de expresar opiniones.

*Tuesday - Class debate*

Professor Chen teaches opinion expressions in Winnipeg.
"I think... In my opinion... I find..."
Students learn different ways to express opinions.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match opinion starter to when to use it.",
          pairs: [
            { left: "I think...", right: "Casual conversations" },
            { left: "In my opinion...", right: "Formal debates/essays" },
            { left: "I find...", right: "Sophisticated opinions" }
          ],
          a: "completed",
          tip: "Different levels of formality!",
          successTip: "'I think' = everyday! 'In my opinion' = formal! 'I find' = sophisticated! 💬"
        },
        {
          type: "mc",
          q: "Most common in everyday conversation:",
          options: ["I think soccer is fun", "In my opinion, soccer is fun", "I find soccer enjoyable"],
          a: "I think soccer is fun",
          tip: "'I think' = most natural!",
          successTip: "I think! Used 90% of the time in casual talk! ⚽"
        },
        {
          type: "mc",
          q: "'I find yoga very relaxing.' This means:",
          options: ["I think yoga is very relaxing", "I discover yoga", "I search for yoga"],
          a: "I think yoga is very relaxing",
          tip: "'I find' = I think (more sophisticated)!",
          successTip: "'I find' = sophisticated way to say 'I think'! Same meaning! 🧘"
        },
        {
          type: "match",
          q: "Match structure to example.",
          pairs: [
            { left: "I think + sentence", right: "I think pizza is delicious" },
            { left: "I find + it/them + adjective", right: "I find it delicious" },
            { left: "In my opinion, + sentence", right: "In my opinion, pizza is best" }
          ],
          a: "completed",
          tip: "Three different structures!",
          successTip: "Structures! 'I think' and 'In my opinion' use full sentences. 'I find' uses object + adjective! 📚"
        },
        {
          type: "input",
          q: "Complete: '___ ___, exercise is important.' (formal opinion starter)",
          a: "In my opinion",
          alts: ["I think", "I believe"],
          tip: "Formal = In my opinion!",
          successTip: "In my opinion! Formal starter for debates/essays! 💪"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce (casual): 'Creo que el chocolate es delicioso.'",
          words: ["I", "think", "chocolate", "is", "delicious."],
          a: "I think chocolate is delicious.",
          extra: ["find", "believe", "opinion"],
          tip: "I think = most casual!"
        },
        {
          type: "mc",
          q: "More sophisticated way to say 'I think swimming is relaxing':",
          options: ["I find swimming relaxing", "I find swimming is relaxing", "Swimming I find relaxing"],
          a: "I find swimming relaxing",
          tip: "I find + object + adjective!",
          successTip: "I find swimming relaxing! No 'is'! I find + [thing] + [adjective]! 🏊"
        },
        {
          type: "input",
          q: "Complete (formal): '___ ___ ___, reading is important.' (In my opinion)",
          a: "In my opinion",
          alts: [],
          tip: "Formal debate/essay starter!",
          successTip: "In my opinion! Comma after! Formal expression! 📚"
        },
        {
          type: "order",
          q: "Traduce (sophisticated): 'Encuentro el yoga muy relajante.'",
          words: ["I", "find", "yoga", "very", "relaxing."],
          a: "I find yoga very relaxing.",
          extra: ["think", "is", "that"],
          tip: "I find + activity + adjective!"
        },
        {
          type: "input",
          q: "Give YOUR opinion: '___ ___ ___ is the best exercise.' (I think / In my opinion)",
          a: "I think swimming",
          alts: ["In my opinion swimming", "I think running", "I think cycling", "In my opinion yoga"],
          tip: "Express your real opinion!",
          successTip: "Personal opinion! Use 'I think' (casual) or 'In my opinion' (formal)! Perfect! 🌟"
        }
      ]
    },

    // ===================================================================
    // SET 10: COMPLETE OPINIONS & RECOMMENDATIONS (B1-)
    // ===================================================================
    {
      id: "m3-u8-set10",
      title: "Set 10: Complete Opinions 🏆",
      context: `*Miércoles - Presentación final*

Los estudiantes hacen presentaciones completas sobre sus preferencias.
"Mi deporte favorito es la natación porque es relajante y saludable. Es mi actividad número uno."
Combinan todo lo aprendido.

*Wednesday - Final presentation*

Students make complete presentations about their preferences.
"My favorite sport is swimming because it's relaxing and healthy. It's my number one activity."
They combine everything learned.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "mc",
          q: "Complete opinion: 'I love swimming because it's relaxing, ___ it's also great exercise, ___ I do it three times a week.'",
          options: ["and // so", "but // because", "so // but"],
          a: "and // so",
          tip: "Two reasons (and) → result (so)!",
          successTip: "And + so! Two reasons lead to action! 🏊"
        },
        {
          type: "mc",
          q: "Best complete answer to 'What's your favorite food and why?':",
          options: ["My favorite food is pizza because it's delicious and easy to eat", "Pizza", "I like pizza"],
          a: "My favorite food is pizza because it's delicious and easy to eat",
          tip: "Complete = favorite + reason!",
          successTip: "Complete answer! Favorite + because + two reasons! 🍕"
        },
        {
          type: "match",
          q: "Match sentence part to function.",
          pairs: [
            { left: "My favorite sport is...", right: "States preference" },
            { left: "because it's exciting and fun", right: "Gives reasons" },
            { left: "so I play it every week", right: "Shows result/action" }
          ],
          a: "completed",
          tip: "Complete opinion structure!",
          successTip: "Three parts! Preference + reasons + action! 🎯"
        },
        {
          type: "mc",
          q: "'I enjoy reading because it's relaxing, but I don't have much time, so I only read on weekends.' How many ideas are connected?",
          options: ["Four", "Two", "Three"],
          a: "Four",
          tip: "Count: enjoy, reason, problem, result!",
          successTip: "Four ideas! Enjoy + because + but + so! Complex! 📚"
        },
        {
          type: "input",
          q: "Complete pattern: 'I ___ ___ because ___, ___ I ___ it ___.' (love/like, reason, so, do, frequency)",
          a: "love swimming it's fun so do often",
          alts: ["like reading it's relaxing so read daily"],
          tip: "Love + reason + so + action!",
          successTip: "Complete pattern! Feeling + reason + result! 🌟"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Mi comida favorita es la pasta porque es deliciosa, pero no la como mucho porque no es saludable.'",
          words: ["My", "favorite", "food", "is", "pasta", "because", "it's", "delicious,", "but", "I", "don't", "eat", "it", "much", "because", "it's", "not", "healthy."],
          a: "My favorite food is pasta because it's delicious, but I don't eat it much because it's not healthy.",
          extra: ["so", "and", "very"],
          tip: "Two 'because' + one 'but'!"
        },
        {
          type: "mc",
          q: "Complete recommendation: 'I think everyone should try yoga ___ it's relaxing ___ it reduces stress.'",
          options: ["because // and", "so // but", "but // so"],
          a: "because // and",
          tip: "Should try + reason + additional reason!",
          successTip: "Because + and! Two reasons for recommendation! 🧘"
        },
        {
          type: "input",
          q: "Complete complex: 'I ___ running ___ I ___ it ___, ___ I do it anyway ___ it's healthy.' (hate, find, tiring, but, because)",
          a: "hate because find tiring but because",
          alts: [],
          tip: "Hate (feeling) + find tiring (reason) + but do it (contrast) + because healthy (reason)!",
          successTip: "Complex! Negative feeling + reason + but + positive reason! Advanced! 🏃"
        },
        {
          type: "order",
          q: "Traduce: 'Creo que la natación es el mejor ejercicio porque es relajante y saludable, entonces nado tres veces por semana.'",
          words: ["I", "think", "swimming", "is", "the", "best", "exercise", "because", "it's", "relaxing", "and", "healthy,", "so", "I", "swim", "three", "times", "a", "week."],
          a: "I think swimming is the best exercise because it's relaxing and healthy, so I swim three times a week.",
          extra: ["but", "very", "most"],
          tip: "Opinion + two reasons + result!"
        },
        {
          type: "input",
          q: "Final! YOUR complete opinion: 'My favorite ___ is ___ because ___ and ___, so I ___ it ___.' (Fill ALL blanks!)",
          a: "food pizza it's delicious easy eat often",
          alts: ["sport swimming it's fun healthy do regularly", "hobby reading it's relaxing interesting do daily"],
          tip: "Express YOUR real preference completely!",
          successTip: "✓ COMPLETE OPINION!\nYou can express preferences with reasons and actions! Unit 8 mastered! 🏆"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET: Food Around the World (FUERA DEL ARRAY)
  // ===================================================================
  readingSet: {
    id: "m3-u8-reading",
    title: "Reading: Food Preferences Around the World 🌍",
    category: "reading",

    questions: [
      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 1: Introduction (preguntas 1-2)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Different Tastes**

People around the world have different food preferences. Some foods are popular everywhere, but others are only popular in certain countries. Let's learn about what people like and don't like in different places.`
      },
      {
        type: "mc",
        q: "According to the text, food preferences are:",
        options: ["Different in different countries", "The same everywhere", "Only about popular foods"],
        a: "Different in different countries",
        tip: "Different around the world!",
        successTip: "✓ DIFFERENT!\nPreferences vary by country! 🌍"
      },
      {
        type: "mc",
        q: "The text will talk about:",
        options: ["What people like and don't like", "Only popular foods", "How to cook"],
        a: "What people like and don't like",
        tip: "Likes AND dislikes!",
        successTip: "✓ BOTH!\nWill cover preferences - likes and dislikes! 🎯"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 2: Japan (preguntas 3-4)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Japan**

In Japan, people love sushi and ramen. Most Japanese people eat rice every day because it's a traditional food. They enjoy green tea because it's healthy and refreshing. However, many Japanese people don't like very spicy food. They find it too strong for their taste.`
      },
      {
        type: "input",
        q: "What do Japanese people love?",
        a: "sushi and ramen",
        alts: ["sushi ramen", "ramen and sushi"],
        tip: "Two foods mentioned!",
        successTip: "✓ CORRECT!\nSushi and ramen! 🍣🍜"
      },
      {
        type: "mc",
        q: "Why don't many Japanese people like very spicy food?",
        options: ["Because they find it too strong", "Because it's unhealthy", "Because it's expensive"],
        a: "Because they find it too strong",
        tip: "The reason is in the text!",
        successTip: "✓ REASON!\nThey find it too strong! 🌶️"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 3: Mexico (preguntas 5-6)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Mexico**

Mexican people love spicy food! They enjoy tacos, burritos, and enchiladas because they're flavorful and delicious. Hot sauce is very popular in Mexico because it makes food more exciting. Most Mexicans can't stand bland food - they find it boring. Spicy food is their number one preference.`
      },
      {
        type: "mc",
        q: "What is Mexicans' number one preference?",
        options: ["Spicy food", "Bland food", "Sweet food"],
        a: "Spicy food",
        tip: "Number one = favorite!",
        successTip: "✓ NUMBER ONE!\nSpicy food is their top preference! 🌮"
      },
      {
        type: "input",
        q: "How do Mexicans feel about bland food?",
        a: "can't stand it",
        alts: ["they can't stand it", "can't stand"],
        tip: "Strongest negative expression!",
        successTip: "✓ CAN'T STAND!\nStrongest dislike! They find it boring! 😐"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 4: Italy (preguntas 7-8)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Italy**

Italians love pasta and pizza, but they're very particular about how to make them. They enjoy fresh ingredients because quality is important. Italian people don't like fast food versions of their traditional dishes. They think fast food pizza is not real pizza because it's not made properly. Fresh mozzarella and tomatoes are essential.`
      },
      {
        type: "mc",
        q: "Why do Italians enjoy fresh ingredients?",
        options: ["Because quality is important", "Because they're cheap", "Because they're fast"],
        a: "Because quality is important",
        tip: "Reason is stated!",
        successTip: "✓ REASON!\nQuality matters to Italians! 🍕"
      },
      {
        type: "mc",
        q: "What do Italians think about fast food pizza?",
        options: ["It's not real pizza", "It's delicious", "It's the best"],
        a: "It's not real pizza",
        tip: "They don't like it!",
        successTip: "✓ OPINION!\nThey don't consider it real pizza! 🍕"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 5: Canada (preguntas 9-10)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Canada**

Canadians have diverse food preferences because Canada is a multicultural country. Many Canadians love poutine - fries with gravy and cheese - because it's a traditional Canadian dish. They also enjoy international foods like Chinese, Indian, and Italian cuisine. Most Canadians don't mind trying new foods because they're open to different cultures. Tim Hortons coffee is Canada's favorite drink!`
      },
      {
        type: "input",
        q: "Why do Canadians have diverse food preferences?",
        a: "because Canada is multicultural",
        alts: ["multicultural country", "Canada is multicultural"],
        tip: "The reason is cultural!",
        successTip: "✓ REASON!\nMulticultural = many cultures = diverse tastes! 🇨🇦"
      },
      {
        type: "mc",
        q: "How do most Canadians feel about trying new foods?",
        options: ["They don't mind it", "They hate it", "They never try it"],
        a: "They don't mind it",
        tip: "Open to different cultures!",
        successTip: "✓ COMPLETE!\nYou understand preferences around the world! 🌍"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (FUERA DEL ARRAY)
  // ===================================================================
  reviewSet: {
    id: "m3-u8-review",
    title: "Unit 8 Review",
    category: "review",
    
    questions: [
      {
        type: "mc",
        q: "Review: Strongest negative expression:",
        options: ["I can't stand it", "I don't like it", "I hate it"],
        a: "I can't stand it",
        tip: "Strongest = can't stand!"
      },
      {
        type: "input",
        q: "Review: Complete: 'Do you like pizza?' - 'Yes, I love ___ because ___ delicious.'",
        a: "it it's",
        alts: ["it its"],
        tip: "It (object) + it's (it is)!"
      },
      {
        type: "mc",
        q: "Review: Activity + adjective = 'Yoga is ___'",
        options: ["relaxing", "relaxed", "relax"],
        a: "relaxing",
        tip: "Activity is + -ing adjective!"
      },
      {
        type: "input",
        q: "Review: Connect ideas: 'I love pizza ___ it's delicious.' (reason)",
        a: "because",
        alts: [],
        tip: "Because = reason!"
      },
      {
        type: "mc",
        q: "Review: Complete: 'My ___ food is pasta.'",
        options: ["favorite", "number", "best"],
        a: "favorite",
        tip: "My favorite + category!"
      },
      {
        type: "input",
        q: "Review: Hobby form: 'I enjoy ___.' (swim)",
        a: "swimming",
        alts: [],
        tip: "Enjoy + -ing!"
      },
      {
        type: "mc",
        q: "Review: Contrast: 'I like pizza, ___ I don't eat it often.'",
        options: ["but", "so", "because"],
        a: "but",
        tip: "But = contrast!"
      },
      {
        type: "input",
        q: "Review: Sophisticated opinion: 'I ___ yoga very relaxing.'",
        a: "find",
        alts: [],
        tip: "I find + object + adjective!"
      },
      {
        type: "mc",
        q: "Review: Result: 'Swimming is healthy, ___ I swim every day.'",
        options: ["so", "because", "but"],
        a: "so",
        tip: "So = result!"
      },
      {
        type: "mc",
        q: "Review: Complete scale: love, like, don't mind, don't like, ___, ___",
        options: ["hate, can't stand", "can't stand, hate", "dislike, hate"],
        a: "hate, can't stand",
        tip: "Hate, then can't stand (strongest)!",
        successTip: "🏆 UNIT 8 COMPLETE! Preferences, reasons, and opinions mastered! Ready for Unit 9! 🚀"
      }
    ]
  }
};
